import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface AuthFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  /** Optional helper line under the field. */
  hint?: string;
  /** When set, renders as an error state with a pink border + message. */
  error?: string;
  className?: string;
}

/**
 * Editorial input row — uppercase label above, sharp ink-on-ink input.
 * No floating placeholder bullshit, no SaaS glow ring, no rounded corners.
 */
export const AuthField = ({ label, hint, error, className, ...rest }: AuthFieldProps) => (
  <label className={cn('block', className)}>
    <span className="block font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
      {label}
    </span>
    <input
      {...rest}
      className={cn(
        'mt-2 w-full border bg-ink/40 px-4 py-3',
        'font-display text-base text-text-primary placeholder:text-text-muted',
        'transition-[border-color,background] duration-fast ease-signal',
        'focus:bg-ink/60 focus:outline-none focus:shadow-glow-soft',
        error
          ? 'border-pink focus:border-pink'
          : 'border-white/[0.12] focus:border-pink',
      )}
    />
    {error && (
      <span className="mt-1.5 block font-display text-[10px] font-bold uppercase tracking-widest text-pink">
        — {error}
      </span>
    )}
    {hint && !error && (
      <span className="mt-1.5 block font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
        {hint}
      </span>
    )}
  </label>
);
